import { SafetyRecommendationItem } from '@deckee/api-app-main';
import { createSelector } from 'reselect';
import { AppState } from '../..';
import { MarketplaceListingItem } from './models';

const alphabeticalComparator = (a: { name: string }, b: { name: string }) => {
  if (a.name === b.name) {
    return 0;
  }

  if (a.name < b.name) {
    return -1;
  }

  return 1;
};
const ordinalComparator = (a: { ordinal: number }, b: { ordinal: number }) => {
  if (a.ordinal === b.ordinal) {
    return 0;
  }

  if (a.ordinal < b.ordinal) {
    return 1;
  }

  return -1;
};

const sortSafetyGear = (
  a: SafetyRecommendationItem,
  b: SafetyRecommendationItem,
) => {
  if (a.ordinal || b.ordinal) {
    return ordinalComparator(a, b);
  }

  return alphabeticalComparator(a, b);
};

const sortByDistance = (
  listingA: MarketplaceListingItem,
  listingB: MarketplaceListingItem,
): 1 | -1 | 0 => {
  if (
    listingA.distanceFromRequestLocation ===
    listingB.distanceFromRequestLocation
  ) {
    return 0;
  }

  return listingA.distanceFromRequestLocation <
    listingB.distanceFromRequestLocation
    ? -1
    : 1;
};

export const sortListings = (
  listingA: MarketplaceListingItem,
  listingB: MarketplaceListingItem,
): -1 | 0 | 1 => {
  if (listingA.partner === listingB.partner) {
    return sortByDistance(listingA, listingB);
  }

  if (listingA.partner) {
    return -1;
  }

  return 1;
};

export const selectAllMarketplaceCategories = (state: AppState) =>
  state.marketplace.categories;

export const selectMarkerTypeCategory = createSelector(
  (state: AppState) => state.marketplace.categories,
  (state: AppState, markerType: string) => markerType,
  (categories, markerType) =>
    categories.find((c) => c.markerTypes.includes(markerType)),
);

export const selectSelectedMarketplaceListing = createSelector(
  (state: AppState) => state.marketplace.selectedListing,
  (state: AppState) => state.marketplace.listings,
  (selectedListing, listings) => listings[selectedListing || ''],
);

export const selectSelectedCategory = (state: AppState) =>
  state.marketplace.selectedCategory;

export const selectMarketplaceSearchResults = createSelector(
  (state: AppState) => state.marketplace.searchResults,
  (results) => [...results].sort(sortListings),
);

export const getSelectedCategory = createSelector(
  (state: AppState) => state.marketplace.selectedCategory,
  (state: AppState) => state.marketplace.categories,
  (selectedCategory, categories) =>
    categories.find((c) => c.id === selectedCategory),
);

export const getSuggestedSafetyItems = (
  state: AppState,
): AppState['marketplace']['suggestedSafetyItems'] =>
  [...state.marketplace.suggestedSafetyItems].sort(sortSafetyGear);
